import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import type { ImageToolData } from '@editorjs/image/dist/types/types';
import { cleanHtml, cleanHtmlToPlainText } from '@src/utils/html-utils/html-utils';
import { noop } from '@src/utils/utils';
import type { FC } from 'react';

export interface ExtendedImageToolData extends ImageToolData {
    url?: string;
    linkUrl?: string;
    size?: 'small' | 'medium' | 'full';
    alignment?: 'left' | 'center' | 'right';
}

export interface EditorViewOnlyImageToolProps {
    className?: string;
    blockData: ExtendedImageToolData;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const EditorViewOnlyImageTool: FC<EditorViewOnlyImageToolProps> = props => {
    const { onClick = noop, blockData, className } = props;

    const getSizeClasses = () => {
        const { size = 'full' } = blockData;
        switch (size) {
            case 'small':
                return 'max-w-xs';
            case 'medium':
                return 'max-w-md';
            case 'full':
            default:
                return 'w-full';
        }
    };

    const getAlignmentClasses = () => {
        const { alignment = 'center' } = blockData;
        switch (alignment) {
            case 'left':
                return 'mr-auto';
            case 'right':
                return 'ml-auto';
            case 'center':
            default:
                return 'mx-auto';
        }
    };

    const getImageUrl = () => {
        const { file, url } = blockData;
        return cleanHtml(file?.url || url || '');
    };

    const getImageAlt = () => {
        const { caption } = blockData;
        return cleanHtmlToPlainText(caption || '');
    };

    const getContainerClasses = () => {
        const { withBackground, withBorder } = blockData;
        const classes = ['cdx-block', 'overflow-hidden'];

        if (withBackground) classes.push('bg-gray-200');
        if (withBorder) classes.push('border');

        return classes.join(' ');
    };

    const getCaptionTextAlignClassName = () => {
        const { alignment = 'center', size = 'full' } = blockData;
        if (size === 'full') return '';
        if (alignment === 'left') return 'text-left';
        if (alignment === 'right') return 'text-right';
        return 'text-center';
    };

    const renderCaption = () => {
        const { caption } = blockData;
        if (!caption) return null;

        const textAlign = getCaptionTextAlignClassName();
        const captionClasses = cn('block mt-1 text-sm text-gray-500', textAlign);

        return (
            <Text enableMultiline={true} className={captionClasses} data-placeholder="Caption">
                {cleanHtmlToPlainText(caption)}
            </Text>
        );
    };

    const renderImage = () => {
        const imageUrl = getImageUrl();
        const imageAlt = getImageAlt();
        const sizeClasses = getSizeClasses();
        const alignmentClasses = getAlignmentClasses();

        return <img className={cn(sizeClasses, alignmentClasses)} src={imageUrl} alt={imageAlt} />;
    };

    const renderContent = () => {
        const { linkUrl } = blockData;
        const containerClasses = getContainerClasses();
        const imageElement = renderImage();

        return (
            <div className={containerClasses}>
                {linkUrl ? (
                    <a href={linkUrl} target="_blank" rel="noopener noreferrer">
                        {imageElement}
                    </a>
                ) : (
                    imageElement
                )}
                {renderCaption()}
            </div>
        );
    };

    return (
        <div onClick={onClick} className={cn('image-tool cursor-pointer', className)}>
            {renderContent()}
        </div>
    );
};

export default EditorViewOnlyImageTool;
